import generateActions from 'utils/actions';
import User from 'store/models/user';

export const DELETE_USER = generateActions('users/DELETE_USER');
export const GET_ARTISTS = generateActions('users/GET_ARTISTS');
export const GET_ACTIVITIES = generateActions('users/GET_ACTIVITIES');
export const GET_FOLLOWERS = generateActions('users/GET_FOLLOWERS');
export const GET_PUBLISHED_ALBUMS = generateActions('users/GET_PUBLISHED_ALBUMS');
export const GET_PUBLISHED_SINGLES = generateActions('users/GET_PUBLISHED_SINGLES');
export const GET_POPULAR_SONGS = generateActions('users/GET_POPULAR_SONGS');
export const GET_MY_PLAYLISTS = generateActions('users/GET_MY_PLAYLISTS');
export const GET_PUBLIC_PLAYLISTS = generateActions('users/GET_PUBLIC_PLAYLISTS');
export const GET_PROFILE = generateActions('users/GET_PROFILE');
export const GET_USERS = generateActions('users/GET_USERS');
export const POST_AVATAR = generateActions('users/POST_AVATAR');
export const POST_PLAYLISTS = generateActions('users/POST_PLAYLISTS');
export const PUT_PLAYLISTS = generateActions('users/PUT_PLAYLISTS');
export const PUT_FOLLOW = generateActions('users/PUT_FOLLOW');
export const PUT_UNFOLLOW = generateActions('users/PUT_UNFOLLOW');
export const DELETE_PLAYLISTS = generateActions('users/DELETE_PLAYLISTS');
export const POST_ADD_SONGS_TO_PLAYLIST = generateActions('users/POST_ADD_SONGS_TO_PLAYLIST');
export const PUT_USER = generateActions('users/PUT_USER');
export const SET_USER_FIELD = 'users/SET_USER_FIELD';

/**
 * Updates the user model in state
 * @param {String} field 
 * @param {String} value 
 */
export function setUserField(field, value) {
  return {
    type: SET_USER_FIELD,
    payload: {
      field,
      value,
    },
  };
}

/**
 * Load all users for admin
 */
export function loadUsers(data) {
  return {
    types: GET_USERS,
    promise: {
      url: '/users',
      data,
    },
  };
}

/**
 * Load a user profile by id
 * @param {String} userId
 */
export function getProfile(userId) {
  return {
    types: GET_PROFILE,
    promise: {
      url: `/users/${userId}`,
      reader: {
        model: User,
        root: 'user',
      },
    },
  };
}

/**
 * Load the activities feed for the given user
 * @param {String} userId
 * @param {Number} page 
 */
export function loadFeed(userId, page = 1) {
  return {
    types: GET_ACTIVITIES,
    promise: {
      url: `/users/${userId}/feed?page=${page}`,
    },
  };
}

/**
 * Returns all published albums by the given user
 * @param {String} userId 
 */
export function getPublishedAlbums(userId) {
  return {
    types: GET_PUBLISHED_ALBUMS,
    promise: {
      url: `/artists/${userId}/albums`,
    },
  };
}

/**
 * Returns all published singles by the given user
 * @param {String} userId 
 */
export function getPublishedSingles(userId) {
  return {
    types: GET_PUBLISHED_SINGLES,
    promise: {
      url: `/artists/${userId}/singles`,
    },
  };
}

/**
 * Get most popular songs by the given user
 * @param {String} userId 
 */
export function getPopularSongs(userId) {
  return {
    types: GET_POPULAR_SONGS,
    promise: {
      url: `/artists/${userId}/songs`,
    },
  };
}

/**
 * Creates a new playlist
 * @param {Object} playlist 
 */
export function postPlaylists(playlist) {
  return {
    types: POST_PLAYLISTS,
    promise: {
      url: '/playlists',
      method: 'POST',
      data: playlist,
    },
  };
}

/**
 * Updates a playlists
 * @param {Playlist} playlist
 */
export function putPlaylists(playlist) {
  return {
    types: PUT_PLAYLISTS,
    promise: {
      url: `/playlists/${playlist.id}`,
      method: 'PUT',
      data: {
        title: playlist.title,
        public: playlist.public,
      },
    },
  };
}

/**
 * Removes a playlist that belongs to a user
 * @param {Playlist} playlist 
 */
export function deletePlaylists(playlist) {
  return {
    types: DELETE_PLAYLISTS,
    promise: {
      url: `/playlists/${playlist.id}`,
      method: 'DELETE',
    },
  };
}

/**
 * Get public playlists for the given user
 * @param {String} userId 
 */
export function getPlaylists(userId) {
  return {
    types: GET_PUBLIC_PLAYLISTS,
    promise: {
      url: `/playlists/user/${userId}`,
    },
  };
}

/**
 * Get all private and public playlists for the current user
 */
export function getMyPlaylists() {
  return (dispatch, getState) => {
    const state = getState();
    const { user } = state.auth;

    return dispatch({
      types: GET_MY_PLAYLISTS,
      promise: {
        url: `/playlists/user/${user.id}`,
      },
    });
  };
}

/**
 * Add songs to the given playlists
 * @param {Playlist} playlist 
 * @param {Array} songs 
 */
export function postAddToPlaylist(playlist, songs) {
  return {
    types: POST_ADD_SONGS_TO_PLAYLIST,
    promise: {
      url: `/playlists/${playlist.id}`,
      method: 'POST',
      data: {
        songs: songs.map(sng => sng.id),
      },
    }
  }
}

/**
 * Updates the user information
 */
export function putUser(user) {
  return {
    types: PUT_USER,
    promise: {
      url: `/users/${user.id}`,
      method: 'POST',
      form: {
        ...user,
        image: user.image instanceof File ? user.image : undefined,
      },
    },
  };
}

/**
 * Removes a user from the DB
 * @param {User} user
 */
export function deleteUser(user) {
  return {
    types: DELETE_USER,
    promise: {
      url: `/users/${user.id}`,
      method: 'DELETE',
    },
    data: { user },
  };
}

/**
 * Upload a single file for the user's avatar
 * @param {User} user 
 * @param {File} files
 * @param {Object} crop
 */
export function postAvatar(user, file, crop) {
  return {
    types: POST_AVATAR,
    promise: {
      url: `/users/${user.id}`,
      method: 'POST',
      form: {
        image: file,
        id: user.id,
        name: user.name,
        email: user.email,
        ...crop,
      },
    },
  };
}

/**
 * Load a profile by id, including activity, albums, playlists and settings
 * @param {String} id 
 */
export function loadProfile(id) {
  return (dispatch) => {
    const promises = [];

    promises.push(dispatch(getPlaylists(id)));
    promises.push(dispatch(getProfile(id))
      .then(({ payload: { user } }) => {
        if (user.author) {
          dispatch(getPublishedAlbums(id));
          dispatch(getPopularSongs(id));
          dispatch(getPublishedSingles(id));
        }
      }));

    return Promise.all(promises);
  }
}

/**
 * Loading all artists by the given filters
 * @param {Number} page 
 * @param {Object} filters 
 */
export function loadArtists(page = 1, filters) {
  const data = {
    page,
    authors: true,
    stats: true,
  };

  if (filters.genres.length > 0) {
    data.genres = filters.genres.map(item => item.id).join(',')
  }

  if (filters.countries.length > 0) {
    data.countries = filters.countries.map(item => item.name).join(',')
  }

  if (filters.genders.length > 0) {
    data.genders = filters.genders.map(item => item.id).join(',')
  }

  return {
    types: GET_ARTISTS,
    promise: {
      url: `/users`,
      reader: {
        model: User,
        root: 'artists',
      },
      data,
    },
  }
}

/**
 * Load the followers for a given user
 * @param {Number} userId 
 */
export function loadFollowers(userId) {
  return {
    types: GET_FOLLOWERS,
    promise: {
      url: `/users/${userId}/followers`,
    },
  };
}

/**
 * Current user follows the given user
 * @param {Number} userId 
 */
export function putFollow(userId) {
  return {
    types: PUT_FOLLOW,
    promise: {
      url: `/users/${userId}/follow`,
      method: 'PUT'
    },
  }
}

/**
 * Current user unfollows the given user
 * @param {Number} userId 
 */
export function putUnfollow(userId) {
  return {
    types: PUT_UNFOLLOW,
    promise: {
      url: `/users/${userId}/unfollow`,
      method: 'PUT'
    },
  }
}
